import { useAtom, useAtomValue } from "jotai";
import { FC, useMemo } from "react";
import { modeDePaiementAtom, packAtom } from "../../lib/atoms";
import { Tarif } from "../../lib/interfaces/tarif.interface";
import Box from "../ui/Box";
import Title from "../ui/Title";
import Divider from "../ui/Divider";
import { ModeDePaiement } from "../../lib/enums/mode-de-paiement.enum";
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Label } from "../ui/label";
import { useAutoAnimate } from "@formkit/auto-animate/react";
import { TypePays } from "../../lib/enums/type-pays.enum";

interface Props {
  typePays: string;
  nbRuches: number;
  tarif: Tarif;
  typeAbonnement: "papier" | "numerique";
}

const Checkout: FC<Props> = ({ nbRuches, typePays, tarif, typeAbonnement }) => {
  const [parent] = useAutoAnimate();
  const typeAssu = useAtomValue(packAtom);
  const [modeDePaiement, setModeDePaiement] = useAtom(modeDePaiementAtom);

  const prixAbonnement = useMemo(() => {
    return typeAbonnement === "papier"
      ? typePays === TypePays.FRANCE
        ? tarif.abonnementFrance
        : tarif.abonnementEtranger
      : tarif.abonnementNumerique;
  }, [typePays, typeAbonnement, tarif]);

  const prix = useMemo(() => {
    let total = prixAbonnement;
    if (!!typeAssu) {
      total += nbRuches * typeAssu.prix;
    }
    return total;
  }, [prixAbonnement, typeAssu, nbRuches]);

  return (
    <div className='grid grid-cols-1 md:grid-cols-2 gap-4 mt-7'>
      <div>
        <Title order={2}>Récapitulatif</Title>
        <Box className='flex flex-col gap-0.5 mt-3 text-sm'>
          <div className='flex justify-between gap-2'>
            <div>
              Abonnement {typePays === TypePays.FRANCE ? "France métropolitaine" : "hors France métropolitaine"}
            </div>
            <div className='font-medium'>{(prixAbonnement / 100).toFixed(2)}€</div>
          </div>
          <div className='flex justify-between gap-2'>
            <div>Pack d'options</div>
            <div className='font-medium'>{!!typeAssu ? ((typeAssu.prix * nbRuches) / 100).toFixed(2) : "0"}€</div>
          </div>
          <Divider className='my-1' />
          <div className='flex justify-between gap-2'>
            <div className='font-semibold'>TOTAL</div>
            <div className='font-bold'>{(prix / 100).toFixed(2)}€ TTC</div>
          </div>
        </Box>
      </div>

      {/* Bloc Paiement */}
      <div>
        <Title order={2}>Mode de paiement</Title>
        <Box className='mt-3'>
          <Label htmlFor='mode-paiement'>Mode de paiement</Label>
          <Select value={modeDePaiement} onValueChange={(val: ModeDePaiement) => setModeDePaiement(val)}>
            <SelectTrigger className='w-[280px] mt-0.5'>
              <SelectValue placeholder='Choisir ...' />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                {/* <SelectLabel>Fruits</SelectLabel> */}
                <SelectItem value={ModeDePaiement.CB}>Carte bancaire</SelectItem>
                <SelectItem value={ModeDePaiement.CHQ}>Chèque</SelectItem>
                <SelectItem value={ModeDePaiement.VIR}>Virement</SelectItem>
              </SelectGroup>
            </SelectContent>
          </Select>

          <div ref={parent} className='text-sm text-gray-700 mt-3'>
            {!!modeDePaiement && (
              <>
                {modeDePaiement === ModeDePaiement.CB
                  ? "Vous serez redirigé vers le portail de paiement."
                  : "Les instructions de paiement vous seront données à l'étape suivante."}
              </>
            )}
          </div>
        </Box>
      </div>
    </div>
  );
};

export default Checkout;
